import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { LocalizedComponentClass, LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import * as React from 'react';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { ActivityDestinationViewSearch } from '../../../../../../common/__generated__/api';
import { MessageAnchor } from '../../../../../../components/common/message-anchor';
import { useStores } from '../../../../../../utils/hooks';
import { ActivityFeature } from '../components/activity-feature';

export interface ActivityCardContentTopProps extends LocalizedComponentProps {
  activity: ActivityDestinationViewSearch.ActivityInlineFragment;
}

export const ActivityCardContentTop = withLocalization(
  ({ activity, l10n }: ActivityCardContentTopProps): JSX.Element => {
    const hotelPickup = activity.features.hotelPickup;
    const { formatText } = l10n;
    const { routeStore, analytics } = useStores();

    return (
      <UitkLayoutFlex tag="section" direction="column" data-testid="activity-tile-card-content-top">
        {activity.features.enhancedCleaning && (
          <UitkLayoutFlexItem className="uitk-type-200 all-b-padding-one">
            <ActivityFeature
              label={activity.features.enhancedCleaning.label}
              icon={activity.features.enhancedCleaning.icon_temp}
              data-testid="activity-enhanced-cleaning"
            />
          </UitkLayoutFlexItem>
        )}
        <UitkLayoutFlexItem>
          <UitkHeading
            tag="h3"
            size={6}
            className="custom-wrap-text overflow-wrap uitk-type-bold truncate-lines-3 full-width"
            data-testid="activity-tile-card--title"
          >
            {activity.name}
          </UitkHeading>
        </UitkLayoutFlexItem>
        {activity.duration.formatted && (
          <UitkLayoutFlexItem className="uitk-type-200 all-t-margin-one">
            <ActivityFeature
              label={activity.duration.formatted}
              a11yLabel={formatText('lx.search.activity.duration', activity.duration.formatted)}
              icon={activity.duration.icon}
              data-testid="activity-duration--feature"
            />
          </UitkLayoutFlexItem>
        )}
        {activity.reviewSummary?.reviewRatingMessage && (
          <UitkLayoutFlexItem className="all-t-margin-one">
            <UitkLayoutFlex alignItems="center" data-testid="activity-review-rating">
              <UitkText size={400} weight="bold" theme="emphasis" className="all-r-margin-one">
                {activity.reviewSummary.reviewRatingMessage}
              </UitkText>
              <UitkText size={300} theme="default">
                {activity.reviewSummary.reviewCountMessage}
              </UitkText>
              <Experiment name="LX_3P_Reviews">
                <ExperimentControl />
                <ExperimentVariant bucket={1}>
                  {activity.reviewSummary.verifiedReviewsMessage && (
                    <MessageAnchor
                      dialogId={`review-legal-disclaimer-dialog-${activity.id}`}
                      text={activity.reviewSummary.verifiedReviewsMessage.text}
                      id={`review-info-${activity.id}`}
                      icon={activity.reviewSummary.verifiedReviewsMessage.icon}
                      clickAction={activity.reviewSummary.verifiedReviewsMessage.clickAction}
                      anchorClasses="uitk-type-300 type-gray"
                      routeStore={routeStore}
                      analytics={analytics}
                      isNestedPopup={true}
                      showText={false}
                    />
                  )}
                </ExperimentVariant>
              </Experiment>
            </UitkLayoutFlex>
          </UitkLayoutFlexItem>
        )}
        {activity.features.travelDuration?.formatted && (
          <UitkLayoutFlexItem className="uitk-type-200 all-t-margin-two">
            <ActivityFeature
              label={activity.features.travelDuration.formatted}
              data-testid="activity-travel-duration--feature"
            />
          </UitkLayoutFlexItem>
        )}
        {activity.features.travelDistance?.label && (
          <UitkLayoutFlexItem className="uitk-type-200 all-t-margin-two">
            <ActivityFeature
              label={activity.features.travelDistance.label}
              data-testid="activity-travel-distance--feature"
            />
          </UitkLayoutFlexItem>
        )}
        {hotelPickup && (
          <UitkLayoutFlexItem className="uitk-type-200 all-t-margin-two">
            <ActivityFeature
              label={hotelPickup.label}
              icon={hotelPickup.icon_temp}
              data-testid="activity-hotel-pickup--feature"
            />
          </UitkLayoutFlexItem>
        )}
      </UitkLayoutFlex>
    );
  }
) as LocalizedComponentClass<ActivityCardContentTopProps>;
